import { useEffect, useRef } from "react";

/**
 * Phase 1 stability — `setInterval` wrapper that automatically pauses
 * when the page is hidden (Page Visibility API) and resumes when it
 * becomes visible again.
 *
 * Why we need this:
 *   - iOS Safari freezes the JS execution context for backgrounded
 *     tabs and screen-locked PWAs. When the tab is unfrozen, *every*
 *     pending timer fires at once → cascade of parallel API calls →
 *     if any single one returns 401 (because the JWT silently expired
 *     while the tab slept), the global axios interceptor wipes the
 *     session and bounces the user to /login.
 *   - Battery + 4G/5G allowance are wasted polling a screen no one
 *     is looking at.
 *
 * What this helper does NOT do (out of scope by design):
 *   - It does NOT change endpoints, payloads or business logic of
 *     the wrapped callback.
 *   - It does NOT introduce retries, queues or debouncing of
 *     individual fetches.
 *   - It does NOT modify axios interceptors.
 *
 * Behavior contract:
 *   1. While `document.hidden === false`, the callback is invoked
 *      every `ms` milliseconds (exactly like a plain `setInterval`).
 *   2. When `document.hidden` flips to `true`, the timer is cleared.
 *   3. When `document.hidden` flips back to `false`, the timer is
 *      restarted — but **without** an immediate catch-up call (we
 *      explicitly do NOT replay missed ticks; the next call happens
 *      `ms` ms after the page becomes visible again).
 *   4. The latest `fn` reference is always used (kept in a ref) so
 *      that closing over fresh state in the parent component still
 *      works even when the callback identity changes between renders.
 *   5. Strict guard against double intervals: any pending timer is
 *      cleared before a new one is created.
 *   6. Full cleanup on unmount: timer + visibilitychange listener.
 *
 * Note: `fn` is invoked on a best-effort basis. Errors thrown by
 * `fn` will surface as unhandled promise rejections — callers are
 * expected to handle their own errors (matches the existing
 * `setInterval(fetchData, 10000)` semantics).
 */
export function useIntervalWhenVisible(fn, ms) {
  const fnRef = useRef(fn);

  // Keep the latest callback in a ref so the effect below doesn't
  // need `fn` in its dependency array (and therefore doesn't tear
  // down/re-build the interval on every parent render).
  useEffect(() => { fnRef.current = fn; }, [fn]);

  useEffect(() => {
    if (typeof ms !== "number" || ms <= 0) return undefined;

    let timer = null;

    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const start = () => {
      // Guard against double-start (called twice in a row, e.g. from
      // a flickering visibilitychange).
      stop();
      timer = setInterval(() => {
        try { fnRef.current && fnRef.current(); } catch { /* match plain setInterval semantics */ }
      }, ms);
    };

    const handleVisibility = () => {
      if (typeof document === "undefined") return;
      if (document.hidden) stop();
      else start();
    };

    // SSR / jsdom safety: only attach listener if document exists.
    if (typeof document !== "undefined") {
      // Initial state — start only if currently visible.
      if (!document.hidden) start();
      document.addEventListener("visibilitychange", handleVisibility);
    } else {
      start();
    }

    return () => {
      stop();
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibility);
      }
    };
  }, [ms]);
}
